import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Ruler } from 'lucide-react';
import { ReportInputStatus } from '../../services/userService';

interface Props {
  status: ReportInputStatus;
  /** Path to return to after completing the profile. */
  next: string;
}

/**
 * Prominent, app-wide banner shown while the profile is missing the inputs
 * that are MANDATORY for AI reports (face photo + core measurements). Rendered
 * at the top of every app page via AppLayout; render nothing once complete.
 */
const ProfileGateBanner: React.FC<Props> = ({ status, next }) => {
  if (status.ok) return null;

  return (
    <div className="bg-gradient-to-r from-coral/10 via-golden/10 to-white border-2 border-coral/40 rounded-3xl p-6 shadow-lg">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex -space-x-2 shrink-0">
          <div className="w-12 h-12 rounded-2xl bg-coral text-white flex items-center justify-center shadow-md ring-2 ring-white">
            <Camera size={22} />
          </div>
          <div className="w-12 h-12 rounded-2xl bg-teal text-white flex items-center justify-center shadow-md ring-2 ring-white">
            <Ruler size={22} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-nunito font-bold text-lg text-charcoal">
            Your face photo &amp; body measurements are required for AI styling
          </div>
          <div className="text-sm text-gray-600 mt-1">
            Every report is personalised to your undertone, colour palette and body shape — we can't
            generate one without:
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {!status.hasFace ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-coral/30 text-coral text-xs font-semibold">
                <Camera size={12} /> A clear face photo
              </span>
            ) : null}
            {status.missingMeasurements.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-teal/30 text-teal text-xs font-semibold"
              >
                <Ruler size={12} /> {m}
              </span>
            ))}
          </div>
        </div>
        <Link
          to={`/app/complete-profile?next=${encodeURIComponent(next)}`}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-coral text-white font-semibold hover:bg-coral/90 transition-colors shadow-md shrink-0"
        >
          Add photo &amp; measurements
        </Link>
      </div>
    </div>
  );
};

export default ProfileGateBanner;
